import { createContext, useState, useCallback, useRef, useMemo, useEffect, ReactNode } from 'react'
import debounce from 'lodash/debounce'
import { PendingStatus } from '../components'
import { useQueryString, StateFromQueryCallback, ISearchParams } from '@global/hooks'
import { PendingRow } from '@components/dataset/pending/types'

interface IProps {
  children?: ReactNode | ReactNode[]
}

interface IState {
  redirectPath: string | null
  handleRowClick: (row: PendingRow) => void
  handleSubmissionNameSearch: (e: any) => void
  handleTemplateSearch: (templateId: string) => void
  setStateFromQuery: any
  params: ISearchParams
}

const emptyFn = () => {
  // no nothing
}

export const PendingContext = createContext<{ state: IState }>({
  state: {
    redirectPath: '',
    handleRowClick: emptyFn,
    handleSubmissionNameSearch: emptyFn,
    handleTemplateSearch: emptyFn,
    setStateFromQuery: emptyFn,
    params: {},
  },
})

interface IToolbarSearchReturn {
  params: ISearchParams
  setUrl: (params: ISearchParams) => void
  setStateFromQuery: (params: StateFromQueryCallback) => void
}

/**
 * Manage params state changes through changes of the URLSearchParams.
 * - Trigger state change on param changes to filter results.
 */
const useToolbarSearch = (): IToolbarSearchReturn => {
  const [params, setParams] = useState<ISearchParams>({ order_by: 'updated_at', order: 'desc' })
  const { setUrl, setStateFromQuery } = useQueryString(params, val => {
    setParams(val)
  })

  // Set params on page load; catch refreshes.
  useEffect(() => {
    if (window.location.search) {
      setStateFromQuery(searchParams => {
        switch (typeof searchParams) {
          case 'string':
            setParams({ ...params, term: searchParams })
            break
          default:
            if (!searchParams) return
            setParams(searchParams)
        }
      })
    }
  }, [])

  return {
    params,
    setUrl,
    setStateFromQuery,
  }
}

export const PendingContextProvider = ({ children }: IProps) => {
  const [redirectPath, setRedirectPath] = useState<string | null>(null)
  const { params, setUrl, setStateFromQuery } = useToolbarSearch()

  function handleRowClick(row: PendingRow) {
    const { status, type, id, dataset_id, template_id } = row
    const rowType = status || type

    if (rowType === PendingStatus.SHARED_DRAFT) {
      const pathId = dataset_id || id // IMPORTANT, dataset_id is used for shared drafts.
      setRedirectPath(`/codes/update/${template_id}/${pathId}`)
      return
    }

    if (
      rowType === PendingStatus.PAUSED ||
      rowType === PendingStatus.ERROR ||
      rowType === PendingStatus.AWAITING_APPROVAL ||
      rowType === PendingStatus.REJECTED
    ) {
      setRedirectPath(`/codes/details/${id}/data`)
      return
    }

    setRedirectPath(`/codes/update/${template_id}/${id}`)
  }

  const debounced = useRef(
    debounce(({ event, params }) => {
      const val = event.target.value
      setUrl({
        ...params,
        term: val,
      })
    }, 250),
  ).current

  const handleSubmissionNameSearch = useCallback(event => debounced({ event, params }), [params])

  const handleTemplateSearch = useCallback(
    templateId => {
      if (typeof params !== 'object') return

      setUrl({
        ...params,
        template_id: templateId,
      })
    },
    [params],
  )

  const state = {
    redirectPath,
    params,
    handleRowClick,
    handleSubmissionNameSearch,
    handleTemplateSearch,
    setStateFromQuery,
  }
  const value = useMemo(() => ({ state }), [state])

  return <PendingContext.Provider value={value}>{children}</PendingContext.Provider>
}
